import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Formación y coaching";
const heroImage = "https://net-basis.net/Bilder/ict-training-coaching-dach-market-entry.webp";
const heroAlt = "Un empresario visualiza metas de ventas, liderazgo y competencia intercultural en el mercado DACH.";
const heroClaimLine1 = "¡Guiados hacia\nel objetivo!";
const benefitsTitle = "Cómo se beneficia de nuestra experiencia en __formación y coaching__:";
const p1Text = "Ya sea la definición general de la estrategia y las estructuras para impulsar y liderar dicho negocio, o el enfoque en descripciones de proyectos claramente definidas con hitos acordados, cálculos de costes y el \"Return on Investment\", o todas las actividades relacionadas con las ventas y el marketing: un proyecto de entrada al mercado conlleva muchos temas esenciales y fundamentales que requieren una atención y un tratamiento adecuados si desea que este plan tenga éxito.";
const p2Text = "Se vuelve aún más difícil cuando todo debe hacerse para un mercado extranjero, en otro idioma, en otro entorno y dentro de otra cultura.";
const p3Text = "Si, como fabricante, decide a pesar de todo aventurarse y llevar a cabo la entrada al mercado de forma autónoma y con su propio personal, NetBasis International puede, no obstante, brindarle su apoyo y acompañarle de forma global o en áreas específicas con capacitaciones o coachings individuales. Al hacerlo, nos enfocamos conscientemente en la transferencia de conocimientos y habilidades, colaborando con empleados individuales o equipos enteros de su empresa en consonancia con su objetivo. Nuestro compromiso con nosotros mismos es actuar con toda la flexibilidad requerida, ofrecer una calidad excepcional y convertirnos, con el tiempo, en su asesor de confianza.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Reuniones preparatorias para la definición precisa de los objetivos de formación y coaching",
  "Definición de una visión clara para guiar con seguridad la toma de decisiones y las medidas a adoptar",
  "Enfoque integral de todas las áreas esenciales para el desarrollo sostenible de su negocio",
  "Evaluación fundamentada de la situación, así como un análisis estructurado de problemas y potenciales",
  "Orientación estratégica para el desarrollo claro de su estrategia de entrada al mercado",
  "Creación de un plan de acción individual con objetivos de gestión de proyectos medibles",
  "Análisis orientado al mercado de productos, soluciones y la competencia relevante",
  "Presentación óptima de productos y soluciones, incluyendo la auto-presentación eficaz ante los tomadores de decisiones",
  "Establecimiento del modelo adecuado para construir relaciones con clientes potenciales de cara a la conclusión de negocios",
  "Prospección comercial adaptada al mercado, excelente servicio al cliente y fidelización a largo plazo",
  "Apoyo dirigido a sus empleados para que el trabajo de su proyecto sea demostrablemente eficaz",
  "Desarrollo sostenible de los mejores equipos y liderazgo eficaz de grupos y equipos",
  "Provisión de apoyo estratégico permanente para el CEO, la junta directiva y la gestión",
  "Realización de sesiones de coaching a medida con medidas de seguimiento claras",
];

export default function FormacionYCoachingEsPage() {
  const strongName = COMPANY_NAME;
  const p1Parts = p1Text.split(strongName);
  const p2Parts = p2Text.split(strongName);
  const p3Parts = p3Text.split(strongName);

  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Formación & Coaching | NetBasis International"
        description="Capacitaciones y coachings orientados a la práctica para equipos de ventas para el despliegue exitoso de estrategias B2B."
        ogUrl="https://net-basis.net/es/servicios/formacion-y-coaching"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[8%] md:pt-[calc(9%-1.5cm)] lg:pt-[calc(8%-1.5cm)] pl-6 md:pl-[calc(1.5rem+1cm)] pr-4 pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-left training-text-slide"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                    lineHeight: '1.5',
                  }}
                >
                  {heroClaimLine1.split('\n').map((line, i) => (
                    <span key={i} className="block">{line}</span>
                  ))}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="text-foreground-950 leading-relaxed text-sm md:text-base font-semibold mb-4">
              Su socio de confianza para una formación individual y un coaching estratégico en la región DACH. Acompañamos a los fabricantes extranjeros en su entrada autónoma al mercado y aseguramos el éxito de sus equipos locales mediante una transferencia de conocimientos dirigida.
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p3Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p3Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>

            </div>

            {/* Benefits heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i} className="mr-[0.15em]">{part}</em> : part
              )}
            </h2>

            {/* Benefits with checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>

            {/* Widget 6 */}
            <p className="font-heading text-sm md:text-base text-primary-600 font-bold mt-8 md:mt-10 mb-3 md:mb-4">
              ¿Por qué cursos de formación, seminarios y coaching empresarial?
            </p>

            {/* Widget 7 */}
            <p className="text-foreground-950 leading-relaxed text-sm md:text-base">
              Su visión de la apertura de uno o varios mercados nuevos recibe una estructura perfectamente adaptada y un enfoque aplicable sobre el terreno. Esto asegura la armonía con toda su cultura corporativa y garantiza que su proyecto internacional se convierta en un éxito sostenible.
            </p>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}